import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { User } from '../types/auth';
import { DEVELOPER_THEME } from '../types/common';
import { createUser, getUsers, updateUser } from '../api/appUsers';

interface UserFormData {
  email: string;
  password: string;
  password_confirm?: string;  // パスワード確認用
  first_name: string;
  last_name: string;
  role: 'ADMIN' | 'STAFF';
  is_active: string;
  is_developer: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [ users, setUsers ] = useState<User[]>([]);
  const [ loading, setLoading ] = useState<boolean>(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ openDialog, setOpenDialog ] = useState<boolean>(false);
  const [ dialogMode, setDialogMode ] = useState<'add' | 'edit'>('add');
  const [ selectedUser, setSelectedUser ] = useState<User | null>(null);
  const [ formData, setFormData ] = useState<UserFormData>({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    role: 'STAFF',
    is_active: "true",
    is_developer: "false"
  });
  // 初期フォームデータ（変更検出用）
  const [ initialFormData, setInitialFormData ] = useState<UserFormData>({
    email: '',
    password: '',
    password_confirm: '',
    first_name: '',
    last_name: '',
    role: 'STAFF',
    is_active: "true",
    is_developer: "false"
  });
  // フォームに変更があるか追跡する
  const [ formChanged, setFormChanged ] = useState<boolean>(false);
  // 初回レンダリング時のみユーザー一覧を取得
  const initialFetchRef = useRef<boolean>(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      initialFetchRef.current = true;
      fetchUsers();
    }
  }, []);
  const fetchUsers = async() => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      // データがオブジェクトで、resultsプロパティがある場合（ページネーションレスポンス）
      if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
        setUsers(data.results);
      }
      // データが直接配列の場合
      else if (Array.isArray(data)) {
        setUsers(data);
      }
      // データが無効な場合
      else {
        console.error('不正なレスポース形式:', data);
        setUsers([]);  // 空の配列をセット
        setError('APIから不正な形式のデータを受信しました');
      }
      setLoading(false);
    } catch (err) {
      console.error('ユーザー取得エラー:', err);
      setError('ユーザー情報の取得に失敗しました');
      setUsers([]); // エラー時は空の配列をセット
      setLoading(false);
    }
  };
  const handleOpenDialog = (mode: 'add' | 'edit', user?: User) => {
    setDialogMode(mode);
    let newFormData: UserFormData;
    if (mode === 'edit' && user) {
      setSelectedUser(user);
      newFormData = {
        email: user.email,
        password: '', // パスワードは編集時には空にする
        password_confirm: '', // パスワード確認も空にする
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        is_active: user.is_active ? "true" : "false",
        is_developer: user.is_developer ? "true" : "false"
      };
    } else {
      setSelectedUser(null);
      newFormData = {
        email: '',
        password: '',
        password_confirm: '',
        first_name: '',
        last_name: '',
        role: 'STAFF',
        is_active: "true",
        is_developer: "false"
      };
    }
    setFormData(newFormData);
    // 初期データも同じ値で設定（変更検出用）
    setInitialFormData({ ...newFormData });
    // 変更フラグをリセット
    setFormChanged(false);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleInputChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);
    checkFormChanged(newFormData);
  };
  // スイッチ変更ハンドラー
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: checked ? "true" : "false"
    };
    setFormData(newFormData);
    checkFormChanged(newFormData);
  };
  // フォームに変更があるかチェック
  const checkFormChanged = (newData: UserFormData) => {
    // フィールドの変更をチェック
    const hasBasicFieldChanges =
      newData.email !== initialFormData.email ||
      newData.first_name !== initialFormData.first_name ||
      newData.last_name !== initialFormData.last_name ||
      newData.role !== initialFormData.role ||
      newData.is_active !== initialFormData.is_active ||
      newData.is_developer !== initialFormData.is_developer;
    // パスワードフィールドの変更をチェック
    const hasPasswordChange =
      newData.password !== '' &&
      newData.password === newData.password_confirm;
    setFormChanged(hasBasicFieldChanges || hasPasswordChange);
  };
  const handleSubmit = async() => {
    try {
      // パスワード確認のチェック
      if ((dialogMode === 'add' || (dialogMode === 'edit' && formData.password)) &&
        formData.password !== formData.password_confirm) {
        setError('パスワードが一致しません');
        return;
      }
      // データの準備
      // 編集時は必要なフィールドのみを抽出
      let submittedData: any = {};
      if (dialogMode === 'edit') {
        // 編集時は変更されたフィールドだけをコピー（空パスワードは送信しない）
        submittedData = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          is_active: formData.is_active === "true",
          is_developer: formData.is_developer === "true"
        };
        // パスワードが入力された場合のみ追加
        if (formData.password) {
          submittedData.password = formData.password;
        }
      } else {
        // 新規作成時は全フィールド
        submittedData = {
          ...formData,
          is_active: true,
          is_developer: formData.is_developer === "true",
          // usernameフィールドが必要なので、emailを使用
          username: formData.email
        };
      }
      if (dialogMode === 'add') {
        // 新規ユーザー作成
        await createUser(submittedData);
      } else if (selectedUser) {
        // ユーザー更新
        await updateUser(selectedUser.id, submittedData);
      }
      // 成功後、初期データを更新して変更フラグをリセット
      setInitialFormData({ ...formData });
      setFormChanged(false);
      handleCloseDialog();
      fetchUsers();
    } catch (err: any) {
      console.error('ユーザー保存エラー:', err);
      // 詳細なエラーメッセージを表示
      if (err.response?.data) {
        console.log('エラー詳細:', err.response.data);
        // オブジェクトの場合はキーごとにエラーメッセージを生成
        if (typeof err.response.data === 'object') {
          const errorMessages = Object.entries(err.response.data)
          .map(([ key, value ]) => `${key}: ${value}`)
          .join(', ');
          setError(`保存エラー: ${errorMessages}`);
        } else {
          setError(`保存エラー: ${err.response.data}`);
        }
      } else {
        setError('ユーザー情報の保存に失敗しました');
      }
    }
  };
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '未ログイン';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP');
  };
  // 管理者以外はアクセスできないようにする
  if (!user || user.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace/>;
  }
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon/>}
            onClick={() => handleOpenDialog('add')}
          >
            新規ユーザー
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress/>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="user management table">
            <TableHead>
              <TableRow sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                borderBottom: '2px solid rgba(25, 118, 210, 0.5)'
              }}>
                <TableCell sx={{ fontWeight: 'bold' }}>ロール</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>名前</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>メールアドレス</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>開発者モード</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>最終ログイン</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={theme => ({
                    '&:nth-of-type(odd)': { backgroundColor: user.is_active ? 'action.hover' : '#f5f5f5' },
                    ...(user.is_active ? {} : {
                      backgroundColor: '#f8f8f8', // 非アクティブユーザーは薄いグレーに
                      boxShadow: 'inset 0 0 5px rgba(220, 220, 220, 0.5)', // 少し影を付ける
                      opacity: 0.7, // 少し透明に
                      '& td': {color: '#777'}, // テキストは少し薄く
                      borderLeft: '3px solid #dddddd', // 左側に目印の線
                    })
                  })}>
                  <TableCell>
                    {user.is_superuser ? (
                      <Chip
                        label="システム管理者"
                        variant="outlined"
                        color="default"
                        size="small"
                        sx={{ borderColor: 'grey.500' }}
                      />
                    ) : user.role === 'ADMIN' ? (
                      <Chip
                        label="管理者"
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="スタッフ"
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ borderColor: 'primary.light' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{user.last_name} {user.first_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.is_developer ? (
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'inline-block',
                          fontWeight: 'bold',
                          color: DEVELOPER_THEME.DARK,
                          border: `1px solid ${DEVELOPER_THEME.DARK}`,
                          borderLeft: `4px solid ${DEVELOPER_THEME.DARK}`,
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}
                      >
                        ON
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          display: 'inline-block',
                          color: '#616161', // gray[700]
                          borderBottom: '1px dotted #9e9e9e',
                          px: 1,
                          py: 0.25,
                          opacity: 0.8,
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}
                      >
                        OFF
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.last_login)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog('edit', user)}
                      size="small"
                      disabled={user.is_superuser} // システム管理者は編集不可
                      title={user.is_superuser ? "システム管理者は編集できません" : "編集"}
                    >
                      <EditIcon/>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ユーザー追加/編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? '新規ユーザー追加' : 'ユーザー情報編集'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="メールアドレス"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="パスワード"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              required={dialogMode === 'add'}
              helperText={dialogMode === 'edit' ? '変更する場合のみ入力してください' : ''}
            />
            <TextField
              label="パスワード (確認)"
              name="password_confirm"
              type="password"
              value={formData.password_confirm}
              onChange={handleInputChange}
              fullWidth
              required={dialogMode === 'add' || !!formData.password}
              helperText={dialogMode === 'edit' ? 'パスワードを変更する場合のみ入力してください' : ''}
            />
            <TextField
              label="姓"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="名"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>ロール</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label="ロール"
              >
                <MenuItem value="ADMIN">管理者</MenuItem>
                <MenuItem value="STAFF">スタッフ</MenuItem>
              </Select>
            </FormControl>
            {/* ステータスは編集時のみ表示 */}
            {dialogMode === 'edit' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>ステータス</InputLabel>
                  <Select
                    name="is_active"
                    value={formData.is_active}
                    onChange={handleInputChange}
                    label="ステータス"
                  >
                    <MenuItem value={"true"}>有効</MenuItem>
                    <MenuItem value={"false"}>無効 (ログインできなくなります)</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_developer"
                        checked={formData.is_developer === "true"}
                        onChange={handleSwitchChange}
                        color="primary"
                      />
                    }
                    label="開発者モード"
                  />
                  <FormHelperText>
                    開発者モードを有効にすると、システム内部情報などの追加情報が表示されます
                  </FormHelperText>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formChanged || loading}
          >
            {loading ? <CircularProgress size={24}/> : (dialogMode === 'add' ? '追加' : '更新')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default UserManagement;
